import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Images, Loader2, CloudOff, ChevronRight } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { Panel } from "@/components/common/Panel";
import { EmptyState } from "@/components/common/EmptyState";
import { PLAN_CLS } from "@/lib/nav";
import { useAuth } from "@/context/AuthContext";

const mb = (b) => ((b || 0) / 1048576).toFixed(1);

export default function MedienGlobal() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const scope = user?.role === "super_admin" ? "all" : "customers";
  const [state, setState] = useState(null);

  const load = useCallback(() => {
    setState(null);
    api.get("/organizations/summary/all-media", { params: { scope } })
      .then((r) => setState(r.data))
      .catch(() => setState({ service_available: false, tenants: [] }));
  }, [scope]);
  useEffect(() => { load(); }, [load]);

  if (state === null)
    return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-fundio-gold" /></div>;

  const tenants = (state.tenants || []).filter((t) => t.count > 0 || true);

  return (
    <div data-testid="medien-page">
      <PageHeader title="Medien & Galerie" subtitle="Medienübersicht je FUNDIO-Kunde. Verwaltung (Ansehen/Löschen) erfolgt im Kundendetail → Medien & Galerie." />

      <div data-testid="medien-note" className="mb-4 flex items-center gap-2 rounded-lg border border-fundio-border bg-fundio-subtle px-4 py-2 text-xs text-fundio-muted">
        <Images className="h-4 w-4" /><span>Medien werden pro Kunde verwaltet – Kunde öffnen und den Tab «Medien & Galerie» nutzen.</span>
      </div>

      {!state.service_available ? (
        <div data-testid="medien-unavailable" className="card-surface flex flex-col items-center rounded-xl px-6 py-14 text-center">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-status-danger-bg"><CloudOff className="h-6 w-6 text-status-danger" /></span>
          <p className="mt-4 text-base font-semibold text-fundio-ink">FUNDIO Tenant Service nicht erreichbar</p>
        </div>
      ) : (
        <Panel title={`Medien je Kunde (${tenants.length})`} testid="medien-panel">
          {!tenants.length ? (
            <EmptyState icon={Images} title="Keine Medien" description="Es sind derzeit keine Medienobjekte hinterlegt." testid="medien-empty" />
          ) : (
            <div className="overflow-x-auto"><table className="w-full text-sm">
              <thead><tr className="border-b border-fundio-border text-left text-xs uppercase tracking-wide text-fundio-faint">
                <th className="px-2 py-2 font-medium">Kunde</th><th className="px-2 py-2 font-medium">Plan</th>
                <th className="px-2 py-2 font-medium">Bilder</th><th className="px-2 py-2 font-medium">Belegt (MB)</th><th className="px-2 py-2" />
              </tr></thead>
              <tbody className="divide-y divide-fundio-border">
                {tenants.map((t) => (
                  <tr key={t.tenant_id} data-testid={`medien-row-${t.tenant_id}`} onClick={() => navigate(`/brockis/${t.tenant_id}`)} className="cursor-pointer hover:bg-fundio-subtle">
                    <td className="px-2 py-3 font-medium text-fundio-ink">{t.company_name || t.tenant_id}{t.is_demo && <span className="ml-2 rounded border border-status-warning-border bg-status-warning-bg px-1.5 py-0.5 font-mono text-[10px] font-semibold text-status-warning">QA</span>}</td>
                    <td className="px-2 py-3">{t.plan ? <span className={`inline-flex rounded-md border px-2 py-0.5 text-xs font-semibold ${PLAN_CLS[t.plan] || PLAN_CLS.FREE}`}>{t.plan}</span> : "—"}</td>
                    <td className="px-2 py-3 text-fundio-ink">{t.count}</td>
                    <td className="px-2 py-3 text-fundio-muted">{mb(t.total_bytes)}</td>
                    <td className="px-2 py-3 text-right"><ChevronRight className="ml-auto h-4 w-4 text-fundio-faint" /></td>
                  </tr>
                ))}
              </tbody>
            </table></div>
          )}
        </Panel>
      )}
    </div>
  );
}
